import { useCallback } from 'react'
import { useQuery, useQueryClient } from 'react-query'
import { useAppStore } from '@/store/useAppStore'
import { getRecentViews, RECENT_VIEWS_LIMIT } from '../api/recentViewsApi'
import type { RecentCardView, RecentViewsResponse } from '../types'

export const RECENT_VIEWS_QUERY_KEY = ['user', 'recent'] as const

/**
 * Список недавно просмотренного.
 *
 * Запрос ждёт авторизации, как и карточки: без неё бэк ответит 401, а список
 * всё равно нечем будет наполнить — названия и картинки берутся из `/cards`.
 *
 * Ошибку наружу не выносим: блок просто не показывается. Недавно
 * просмотренное — украшение главной, и ронять из-за него экран нельзя.
 */
export const useRecentViewsQuery = () => {
  const authStatus = useAppStore(s => s.authStatus)

  return useQuery<RecentViewsResponse>(
    RECENT_VIEWS_QUERY_KEY,
    () => getRecentViews(RECENT_VIEWS_LIMIT),
    {
      enabled: authStatus === 'success',
      staleTime: 60_000,
      retry: 1,
    }
  )
}

/**
 * Отметить карточку просмотренной.
 *
 * Список правим прямо в кеше, а не ждём бэк. Просмотр он записывает сам при
 * выдаче карточки, но пока человек её читает, главная размонтирована и запрос
 * списка неактивен — перечитывать его некому. Возвращаясь, человек видел
 * прежний список, и карточка появлялась только после перезагрузки страницы.
 *
 * Перезапрос здесь намеренно не запускаем: бэк может ещё не успеть записать
 * просмотр, и свежий ответ затёр бы только что добавленную карточку. Вместо
 * этого помечаем список устаревшим — главная перечитает его при открытии и
 * подставит порядок с бэка, который знает и про другие устройства.
 */
export const useMarkRecentlyViewed = () => {
  const queryClient = useQueryClient()

  return useCallback(
    (cardId: number) => {
      queryClient.setQueryData<RecentViewsResponse>(
        RECENT_VIEWS_QUERY_KEY,
        previous => {
          const viewed: RecentCardView = {
            card_id: cardId,
            last_viewed_at: new Date().toISOString(),
          }
          // Карточку могли открывать и раньше — она переезжает в начало, а не
          // задваивается.
          const rest = (previous?.recent ?? []).filter(
            item => item.card_id !== cardId
          )
          return { recent: [viewed, ...rest].slice(0, RECENT_VIEWS_LIMIT) }
        }
      )

      queryClient.invalidateQueries(RECENT_VIEWS_QUERY_KEY, {
        refetchActive: false,
        refetchInactive: false,
      })
    },
    [queryClient]
  )
}
